import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';

import BusinessAssessment from '../components/organisms/BusinessAssessment';
import ConversationalQuiz from '../components/organisms/BusinessAssessment/ConversationalQuiz';
import { DynamicSkeleton } from '@/components/DynamicSkeleton';
import EventCatalogue from './Event/EventCatalogue';
import { FullPageLoader } from '@/components/atoms';
import { HELPERS } from '@/utils';
import PATH from './path';
import RegisterSuccess from './Auth/components/RegisterSuccess';
import ScrollToAnchor from '@/utils/ScrollToAnchor';
import { useSelector } from 'react-redux';

// Lazy load components
const Home = lazy(() => import('./Home/Home'));
const Catalog = lazy(() => import('./Catalog/Catalog'));
const Certificate = lazy(() => import('./Certificate/Certificate'));
const CourseDetail = lazy(() => import('./Course/CourseDetail'));
const CreateNewPassword = lazy(() => import('./Auth/CreateNewPassword'));
const EventDetail = lazy(() => import('./Event/EventDetail'));
const FAQ = lazy(() => import('./FAQ/FAQ'));
const ForgotPassword = lazy(() => import('./Auth/ForgotPassword'));
const Login = lazy(() => import('./Auth/Login'));
const OTP = lazy(() => import('./Auth/OTP'));
const Payment = lazy(() => import('./Payment/Payment'));
const PaymentBulk = lazy(() => import('./Payment/PaymentBulk'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy/PrivacyPolicy'));
const Profile = lazy(() => import('./Profile/Profile'));
const ProfileChangePassword = lazy(() => import('./Profile/ProfileChangePassword'));
const ProfileUpdate = lazy(() => import('./Profile/ProfileUpdate'));
const Register = lazy(() => import('./Auth/Register'));
const ResetPassword = lazy(() => import('./Auth/ResetPassword'));
const ActivationLanding = lazy(() => import('./Auth/ActivationLanding'));
const StatusPayment = lazy(() => import('./Payment/StatusPayment'));
const SubscriptionPackage = lazy(() => import('./SubscriptionPackage/SubscriptionPackage'));
const LearningProgramPackage = lazy(() => import('./LearningProgram/SubscriptionPackage'));
const TermsAndCondition = lazy(() => import('./TermsAndCondition/TermsAndCondition'));
const Ticket = lazy(() => import('./Ticket/Ticket'));
const TransactionCancelled = lazy(() => import('./Transaction/TransactionCancelled'));
const TransactionDetail = lazy(() => import('./Transaction/TransactionDetail'));
const TransactionList = lazy(() => import('./Transaction/TransactionList'));
const LearningProgram = lazy(() => import('./LearningProgram/Landing'));
const DetailProgram = lazy(() => import('./LearningProgram/DetailProgram'));
const NotFound = lazy(() => import('./NotFoud'));
const NewLanding = lazy(() => import('./NewLanding/landing'));
const Article = lazy(() => import('./Article/ArticlePage'));
const ArticleDetail = lazy(() => import('./Article/ArticleDetailPage'));
const GuestTransactionStatus = lazy(() => import('./Transaction/GuestTransactionStatus'));
const CartPage = lazy(() => import('./Cart/CartPage'));
const AssessmentHistory = lazy(() => import('./Assessment/AssessmentHistory'));
const AssessmentHistoryDetail = lazy(() => import('./Assessment/AssessmentHistoryDetail'));
const AssessmentQuizJourney = lazy(() => import('../components/organisms/AssessmentQuizJourney'));

// Configuration for different route skeletons
const routeSkeletons = {
  [PATH.LANDING_COURSE_PAGE]: {
    layout: 'default',
    contentBlocks: [
      { type: 'banner', height: 400 },
      { type: 'grid', columns: 3, items: 6 },
    ],
  },
  [PATH.HOME]: {
    layout: 'default',
    contentBlocks: [
      {
        type: 'hero',
        height: 600,
        titleWidth: 300,
        subtitleWidth: 500,
        buttonCount: 2,
        imageHeight: 400,
      },
      { type: 'content', height: 200 }, // Untuk konten tambahan di bawah hero
      {
        type: 'grid',
        columns: 3,
        items: 3,
        itemHeight: 150,
      }, // Untuk fitur atau highlight section
    ],
  },
  [PATH.CATALOG]: {
    layout: 'sidebar',
    contentBlocks: [
      { type: 'filters', height: 100 },
      { type: 'grid', columns: 4, items: 12 },
    ],
  },
  [PATH.COURSE]: {
    layout: 'default',
    contentBlocks: [
      { type: 'header', height: 200 },
      { type: 'tabs', count: 3 },
      { type: 'content', height: 600 },
    ],
  },
  [PATH.EVENT]: {
    layout: 'default',
    contentBlocks: [
      { type: 'header', height: 200 },
      { type: 'tabs', count: 3 },
      { type: 'content', height: 600 },
    ],
  },
  // Add more route configurations as needed
};

function ProtectedRoute(props) {
  const { location } = props;
  const { loggedIn } = useSelector((state) => state.auth);
  const queryParam = `redirect=${location.pathname}`;
  return loggedIn ? <Route {...props} /> : <Redirect to={`${PATH.AUTH_LOGIN}?${queryParam}`} />;
}

function AuthenticationRoute(props) {
  let redirectPath = PATH.HOME;
  const { location } = props;
  const { loggedIn } = useSelector((state) => state.auth);

  if (location.search) {
    const { redirect } = HELPERS.queryStringToObject(location.search);
    redirectPath = redirect;
  }

  return loggedIn ? <Redirect to={redirectPath} /> : <Route {...props} />;
}

function AssessmentRoute(props) {
  const { loggedIn } = useSelector((state) => state.auth);
  return loggedIn ? <Route {...props} /> : <Redirect to={PATH.ASSESSMENT_JOURNEY} />;
}

function Routes() {
  const location = useLocation();

  const [currentSkeleton, setCurrentSkeleton] = useState(null);

  useEffect(() => {
    const isGuestRoute = location.pathname.startsWith(PATH.GUEST_TRANSACTION_STATUS.split('/:')[0]);
    if (isGuestRoute) {
      setCurrentSkeleton({ layout: 'minimal' });
    } else {
      const path = Object.keys(routeSkeletons).find((route) => location.pathname.startsWith(route));
      const skeleton = routeSkeletons[path] || routeSkeletons[PATH.HOME];
      setCurrentSkeleton(skeleton);
    }
  }, [location]);

  if (!currentSkeleton) {
    return <DynamicSkeleton {...routeSkeletons[PATH.HOME]} />;
  }

  return (
    <Suspense fallback={<FullPageLoader />}>
      <ScrollToAnchor />

      <Switch>
        <Route exact path={PATH.HOME} render={() => <NewLanding />} />
        <Route exact path={PATH.LANDING_COURSE_PAGE} render={() => <Home />} />
        <Route path={`${PATH.COURSE_CERITIFICATE}/:uuid`} render={() => <Certificate />} />
        <Route path={`${PATH.COURSE}/:uid`} render={() => <CourseDetail />} />
        <Route path={`${PATH.EVENT}/:uid`} render={() => <EventDetail />} />
        <Route path={PATH.CATALOG} render={() => <Catalog />} />
        <Route path={PATH.FAQ} render={() => <FAQ />} />
        <Route path={PATH.SUBSCRIPTION_PACKAGE} render={() => <SubscriptionPackage />} />
        <Route exact path={PATH.LEARNING_PROGRAM} render={() => <LearningProgram />} />
        <Route
          path={PATH.LEARNING_PROGRAM_PACKAGE}
          render={({ match }) => <LearningProgramPackage slug={match.params.slug} />}
        />
        <Route
          path={PATH.LEARNING_PROGRAM_DETAIL}
          render={({ match }) => <DetailProgram slug={match.params.slug} />}
        />
        <Route path={PATH.EVENT} render={() => <EventCatalogue />} />
        <Route exact path={PATH.ARTICLE} render={() => <Article />} />
        <Route exact path={`${PATH.ARTICLE}/:slug`} render={() => <ArticleDetail />} />
        <Route path={PATH.PAYMENT} render={() => <Payment />} />
        <Route path={PATH.PAYMENT_BULK} render={() => <PaymentBulk />} />
        <Route path={PATH.TERMS_AND_CONDITION} render={() => <TermsAndCondition />} />
        <Route path={PATH.PRIVACY_POLICY} render={() => <PrivacyPolicy />} />
        <Route path={PATH.AUTH_REGISTER_SUCCESS} component={RegisterSuccess} />
        <Route path={PATH.AUTH_ACTIVATION_LANDING} render={() => <ActivationLanding />} />
        <Route path={`${PATH.GUEST_TRANSACTION_STATUS}/:token`} render={() => <GuestTransactionStatus />} />
        <AuthenticationRoute path={PATH.AUTH_LOGIN} render={() => <Login />} />
        <AuthenticationRoute path={PATH.AUTH_REGISTER} render={() => <Register />} />
        <AuthenticationRoute path={PATH.AUTH_FORGOT_PASSWORD} render={() => <ForgotPassword />} />
        <AuthenticationRoute path={PATH.AUTH_RESET_PASSWORD} render={() => <ResetPassword />} />
        <AuthenticationRoute path={PATH.AUTH_OTP} render={() => <OTP />} />
        <AuthenticationRoute path={PATH.AUTH_CREATE_NEW_PASSWORD} render={() => <CreateNewPassword />} />
        <ProtectedRoute path={PATH.PROFILE_CHANGE_PASSWORD} render={() => <ProfileChangePassword />} />
        <ProtectedRoute path={PATH.PROFILE_UPDATE} render={() => <ProfileUpdate />} />
        <ProtectedRoute path={PATH.PROFILE} render={() => <Profile />} />
        <ProtectedRoute path={`${PATH.PAYMENT}/:invoiceNumber/status`} render={() => <StatusPayment />} />
        <ProtectedRoute path={PATH.TRANSACTION_CANCELLED} render={() => <TransactionCancelled />} />
        <ProtectedRoute path={`${PATH.TRANSACTION}/:invoiceNumber`} render={() => <TransactionDetail />} />
        <ProtectedRoute path={PATH.TRANSACTION} render={() => <TransactionList />} />
        <ProtectedRoute path={`${PATH.TICKET}/:uuid`} render={() => <Ticket />} />

        <Route exact path={PATH.CART} render={() => <CartPage />} />
        <Route exact path={PATH.ASSESSMENT_JOURNEY} component={AssessmentQuizJourney} />
        <Route path={PATH.ASSESSMENT} exact component={BusinessAssessment} />
        <Route path={PATH.ASSESSMENT_CONVERSATIONAL} exact component={ConversationalQuiz} />
        <ProtectedRoute path={PATH.ASSESSMENT_HISTORY} exact component={AssessmentHistory} />
        <ProtectedRoute path={PATH.ASSESSMENT_HISTORY_DETAIL} exact component={AssessmentHistoryDetail} />
        <Route path="*" render={() => <NotFound />} />
      </Switch>
    </Suspense>
  );
}

export default Routes;
